import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/components/Dialog';
import { NavButton } from '@/shared/components/NavButton';
import { DialogHeading, PlayerInvitationSection, CountdownSection } from './DialogShared';
import { getGameTypeButtonConfig } from '@/shared/utils/gameTypeHelpers';
import { createTextStroke } from '@/styles/textStroke';
import type { PlayerPresence, RoomGameType } from 'shared-types';

const statusTextStyle = createTextStroke('2px');

interface MatchmakingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  gameType: RoomGameType;
  players?: PlayerPresence[];
  isSearching?: boolean;
  countdown?: number | null;
  isStarting?: boolean;
  error?: string | null;
  matchmakingTimeRemaining?: number | null;
  shouldRefresh?: boolean;
  onRefresh?: () => void;
}

export function MatchmakingDialog({
  isOpen,
  onClose,
  gameType,
  players = [],
  isSearching = false,
  countdown = null,
  isStarting = false,
  error = null,
  matchmakingTimeRemaining = null,
  shouldRefresh = false,
  onRefresh
}: MatchmakingDialogProps) {
  const { t } = useTranslation();

  // Get button configuration based on game type
  const { variant: buttonVariant, textKey } = getGameTypeButtonConfig(gameType);
  const buttonText = t(textKey);

  // Dialog variant based on game type
  const dialogVariant = gameType === 'paid' ? 'yellow' : 'blue';

  // Compute status message - only one can be shown at a time
  // Hide matchmaking timer when game countdown has started (4 players joined)
  const statusMessage = (() => {
    if (error) return { text: error, className: 'text-red-500 text-sm' };
    if (isSearching) return { text: t('matchmaking.searching'), className: 'text-white' };
    if (isStarting || countdown !== null) return null; // Game countdown takes over
    if (matchmakingTimeRemaining !== null && matchmakingTimeRemaining > 0) {
      return {
        text: t('matchmaking.waitingForPlayers', { seconds: matchmakingTimeRemaining }),
        className: 'text-white'
      };
    }
    return null;
  })();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      variant={dialogVariant}
      title={t('matchmaking.title')}
    >
      <div className="flex flex-col gap-6">
        {/* Section 1: Game Mode (read-only) */}
        <div className="flex flex-col gap-3">
          <DialogHeading>{t('landing.gameMode')}</DialogHeading>
          <NavButton variant={buttonVariant} disabled className="w-full h-12">
            {buttonText}
          </NavButton>
        </div>

        {/* Section 2: Status message (searching, timer, or error) */}
        {statusMessage && (
          <div className="flex flex-col gap-2 items-center">
            <p
              className={`${statusMessage.className} text-center font-semibold`}
              style={statusTextStyle}
            >
              {statusMessage.text}
            </p>
          </div>
        )}

        {/* Section 3: Refresh prompt when no players found */}
        {shouldRefresh && (
          <div className="flex flex-col gap-3 items-center">
            <p className="text-yellow-400 text-center font-semibold" style={statusTextStyle}>
              {t('matchmaking.noPlayersFound')}
            </p>
            <NavButton variant="green" onClick={onRefresh} className="w-full h-12">
              {t('matchmaking.tryAgain')}
            </NavButton>
          </div>
        )}

        {/* Section 4: Countdown or Player Invitations */}
        {/* Note: isCreator is always false for matchmaking - no "Start Now" button */}
        {!shouldRefresh && (
          <>
            {isStarting && countdown !== null ? (
              <CountdownSection countdown={countdown} isCreator={false} />
            ) : (
              <PlayerInvitationSection players={players} />
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
